// pages/AppointmentDetail.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Edit,
  Trash2,
  MoreVertical,
  Calendar,
  Clock,
  MapPin,
  User,
  Users,
  Video,
  Phone,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Plus,
  ExternalLink,
  Save,
  X as CloseIcon,
  Loader,
  Copy,
  Bell,
  Building2,
  Target,
  Briefcase,
  Activity,
  RefreshCw,
  Search,
  PhoneCall,
} from "lucide-react";
import apiService from "../../services/api";
import ActivityFeed from "../../components/ActivityFeed";
import { useTheme } from "../../contexts/ThemeContext";

const AppointmentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  const [appointment, setAppointment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Related data
  const [attendees, setAttendees] = useState([]);
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);

  // Note creation
  const [newNote, setNewNote] = useState("");
  const [isPrivateNote, setIsPrivateNote] = useState(false);

  // Inline editing
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [savingField, setSavingField] = useState(false);
  const [activityItems, setActivityItems] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [callFetch, setCallFetch] = useState({
    status: "idle",
    result: null,
    error: null,
  });

  const appointmentTypes = [
    { value: "meeting", label: "Online Meeting", icon: Users, color: "blue" },
    { value: "call", label: "Call", icon: Phone, color: "green" },
    { value: "site_visit", label: "Site Visit", icon: MapPin, color: "purple" },
    {
      value: "consultation",
      label: "Consultation",
      icon: Briefcase,
      color: "yellow",
    },
    { value: "demo", label: "Demo", icon: Video, color: "indigo" },
    { value: "follow_up", label: "Follow Up", icon: Calendar, color: "pink" },
    { value: "other", label: "Other", icon: FileText, color: "gray" },
  ];

  const statusOptions = [
    { value: "scheduled", label: "Scheduled", color: "blue" },
    { value: "confirmed", label: "Confirmed", color: "green" },
    { value: "cancelled", label: "Cancelled", color: "red" },
    { value: "completed", label: "Completed", color: "purple" },
    { value: "no_show", label: "No Show", color: "gray" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low", color: "gray" },
    { value: "medium", label: "Medium", color: "blue" },
    { value: "high", label: "High", color: "orange" },
    { value: "urgent", label: "Urgent", color: "red" },
  ];

  const saleOriginatedOptions = [
    { value: "cold_call", label: "Cold Call" },
    { value: "reschedule", label: "Reschedule" },
    { value: "email", label: "Email" },
    { value: "base", label: "Base" },
    { value: "website_enquiry", label: "Website Enquiry" },
    { value: "incoming_call", label: "Incoming Call" },
  ];

  const formatSaleOriginated = (value) => {
    if (!value) return null;
    return saleOriginatedOptions.find((o) => o.value === value)?.label || value;
  };

  useEffect(() => {
    fetchAppointment();
    fetchUsers();
  }, [id]);
  const handleFetchCalls = async () => {
    setCallFetch({ status: "loading", result: null, error: null });
    try {
      const res = await apiService.fetchCallsForAppointment(id, 90);
      setCallFetch({ status: "done", result: res, error: null });
    } catch (err) {
      setCallFetch({
        status: "error",
        result: null,
        error: err.message || "Lookup failed",
      });
    }
  };
  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || response.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  };
  const fetchActivity = async () => {
    try {
      setActivityLoading(true);
      const response = await apiService.getAppointmentActivity(id);
      if (response?.success) {
        setActivityItems(response.data || []);
      }
    } catch (err) {
      console.error("Failed to load activity:", err);
    } finally {
      setActivityLoading(false);
    }
  };
  const fetchAppointment = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAppointment(id);
      console.log("API Response:", response); // Debug log

      // Try different response structures
      const appointmentData = response.appointment || response.data || response;

      if (!appointmentData) {
        throw new Error("No appointment data received");
      }

      setAppointment(appointmentData);
      setAttendees(appointmentData.attendees || []);
    } catch (error) {
      console.error("Error fetching appointment:", error);
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this appointment?")) {
      try {
        await apiService.deleteAppointment(id);
        navigate(-1);
      } catch (error) {
        console.error("Error deleting appointment:", error);
        alert("Failed to delete appointment");
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await apiService.updateAppointment(id, { status: newStatus });
      await fetchAppointment();
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status");
    }
  };

  const handleInlineEdit = (field, currentValue) => {
    setEditingField(field);
    setEditingValue(currentValue || "");
  };

  const handleSaveInlineEdit = async () => {
    if (!editingField) return;

    try {
      setSavingField(true);
      await apiService.updateAppointment(id, {
        [editingField]: editingValue,
      });
      setEditingField(null);
      setEditingValue("");
      await fetchAppointment();
    } catch (error) {
      console.error("Error saving field:", error);
      alert("Failed to save changes");
    } finally {
      setSavingField(false);
    }
  };

  const handleCancelInlineEdit = () => {
    setEditingField(null);
    setEditingValue("");
  };

  const getStatusColor = (status) => {
    const statusObj = statusOptions.find((s) => s.value === status);
    return statusObj?.color || "gray";
  };

  const getPriorityColor = (priority) => {
    const priorityObj = priorityOptions.find((p) => p.value === priority);
    return priorityObj?.color || "gray";
  };

  const getTypeIcon = (type) => {
    const typeObj = appointmentTypes.find((t) => t.value === type);
    return typeObj?.icon || FileText;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    // Strip the Z so the browser treats the value as local/wall-clock time,
    // matching how the DB stores it (literal time, not a UTC instant).
    const date = new Date(dateString.replace("Z", ""));
    return date.toLocaleString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };
  useEffect(() => {
    if (activeTab === "activity") {
      fetchActivity();
    }
  }, [activeTab, id]);
  const formatDuration = (start, end) => {
    if (!start || !end) return "N/A";
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate - startDate;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) {
      return `${diffMins} minutes`;
    }
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return mins > 0
      ? `${hours}h ${mins}m`
      : `${hours} hour${hours > 1 ? "s" : ""}`;
  };

  if (loading) {
    return (
      <div
        className={`flex items-center justify-center h-screen ${isDarkMode ? "bg-[#0D0F12]" : "bg-gray-50"}`}
      >
        <div className="flex flex-col items-center gap-3">
          <Loader className="animate-spin text-blue-500" size={40} />
          <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Loading appointment...
          </p>
        </div>
      </div>
    );
  }

  if (!appointment) {
    return (
      <div
        className={`flex items-center justify-center h-screen ${isDarkMode ? "bg-[#0D0F12]" : "bg-gray-50"}`}
      >
        <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          Appointment not found
        </p>
      </div>
    );
  }

  const TypeIcon = getTypeIcon(appointment.type);

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${isDarkMode ? "bg-[#0D0F12] text-white" : "bg-gray-50 text-gray-900"}`}
    >
      {/* Header */}
      <div
        className={`sticky top-0 z-10 border-b ${isDarkMode ? "bg-[#13161B] border-gray-800" : "bg-white border-gray-200"}`}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1
                  className={`text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {appointment.title}
                </h1>
                <p
                  className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {formatDateTime(appointment.startTime)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* Status Badge */}
              <div className="relative">
                <button
                  className={`px-4 py-2 rounded-lg font-medium text-sm bg-${getStatusColor(appointment.status)}-500/10 text-${getStatusColor(appointment.status)}-400 border border-${getStatusColor(appointment.status)}-500/30 hover:bg-${getStatusColor(appointment.status)}-500/20 transition-colors`}
                >
                  {statusOptions.find((s) => s.value === appointment.status)
                    ?.label || appointment.status}
                </button>
              </div>

              {/* <button
                onClick={() => setShowEditModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors"
              >
                <Edit size={16} />
                Edit
              </button> */}

              {/* <div className="relative">
                <button
                  onClick={() => setShowActionsMenu(!showActionsMenu)}
                  className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <MoreVertical size={20} />
                </button>

                {showActionsMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-[#1a1d24] border border-gray-700 rounded-lg shadow-lg overflow-hidden z-20">
                    <button
                      onClick={() => {
                        setShowActionsMenu(false);
                        // Handle clone
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-800 flex items-center gap-2 text-sm"
                    >
                      <Copy size={16} />
                      Clone Appointment
                    </button>
                    <button
                      onClick={() => {
                        setShowActionsMenu(false);
                        handleDelete();
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-red-900/20 text-red-400 flex items-center gap-2 text-sm"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                )}
              </div> */}
            </div>
          </div>

          {/* Tabs */}
          <div
            className={`flex gap-6 mt-6 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
          >
            <button
              onClick={() => setActiveTab("details")}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                activeTab === "details"
                  ? "text-blue-400 border-b-2 border-blue-400"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-900"
              }`}
            >
              Details
              {activeTab === "details" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("attendees")}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                activeTab === "attendees"
                  ? "text-blue-400 border-b-2 border-blue-400"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-900"
              }`}
            >
              Attendees ({attendees.length})
              {activeTab === "attendees" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("related")}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                activeTab === "related"
                  ? "text-blue-400 border-b-2 border-blue-400"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-900"
              }`}
            >
              Related
              {activeTab === "related" && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("activity")}
              className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                activeTab === "activity"
                  ? "text-blue-400 border-b-2 border-blue-400"
                  : isDarkMode
                    ? "text-gray-400 hover:text-white"
                    : "text-gray-500 hover:text-gray-900"
              }`}
            >
              Activity
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        {activeTab === "details" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Details */}
            <div className="lg:col-span-2 space-y-6">
              {/* Overview Card */}
              <div
                className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Calendar size={20} className="text-blue-400" />
                  Appointment Overview
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Type
                    </label>
                    <div className="flex items-center gap-2">
                      <TypeIcon size={16} />
                      <span className="capitalize">{appointment.type}</span>
                    </div>
                  </div>
                  <div>
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Priority
                    </label>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-${getPriorityColor(appointment.priority)}-500/10 text-${getPriorityColor(appointment.priority)}-400 border border-${getPriorityColor(appointment.priority)}-500/30`}
                    >
                      {appointment.priority?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Start Time
                    </label>
                    <p className={isDarkMode ? "text-white" : "text-gray-900"}>
                      {formatDateTime(appointment.startTime)}
                    </p>
                  </div>
                  <div>
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      End Time
                    </label>
                    <p className={isDarkMode ? "text-white" : "text-gray-900"}>
                      {formatDateTime(appointment.endTime)}
                    </p>
                  </div>
                  <div>
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Duration
                    </label>
                    <p className={isDarkMode ? "text-white" : "text-gray-900"}>
                      {formatDuration(
                        appointment.startTime,
                        appointment.endTime,
                      )}
                    </p>
                  </div>
                  <div>
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Timezone
                    </label>
                    <p className={isDarkMode ? "text-white" : "text-gray-900"}>
                      {appointment.timezone || "UTC"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Sale Originated
                    </label>
                    {editingField === "saleOriginated" ? (
                      <div className="flex gap-2">
                        <select
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                          autoFocus
                        >
                          <option value="">Not Set</option>
                          {saleOriginatedOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={handleSaveInlineEdit}
                          disabled={savingField}
                          className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                        >
                          {savingField ? (
                            <Loader size={16} className="animate-spin" />
                          ) : (
                            <Save size={16} />
                          )}
                        </button>
                        <button
                          onClick={handleCancelInlineEdit}
                          className={`px-3 py-2 rounded-lg ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                        >
                          <CloseIcon size={16} />
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() =>
                          handleInlineEdit(
                            "saleOriginated",
                            appointment.saleOriginated || "",
                          )
                        }
                        className={`cursor-pointer p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-800/50" : "hover:bg-gray-100"}`}
                      >
                        <p
                          className={
                            isDarkMode ? "text-white" : "text-gray-900"
                          }
                        >
                          {formatSaleOriginated(appointment.saleOriginated) || (
                            <span
                              className={
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }
                            >
                              Click to set...
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Location & Meeting Details */}
              <div
                className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <MapPin size={20} className="text-blue-400" />
                  Location & Meeting Details
                </h3>
                <div className="space-y-4">
                  {appointment.location && (
                    <div>
                      <label
                        className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Location
                      </label>
                      {editingField === "location" ? (
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                            autoFocus
                          />
                          <button
                            onClick={handleSaveInlineEdit}
                            disabled={savingField}
                            className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
                          >
                            {savingField ? (
                              <Loader size={16} className="animate-spin" />
                            ) : (
                              <Save size={16} />
                            )}
                          </button>
                          <button
                            onClick={handleCancelInlineEdit}
                            className={`px-3 py-2 rounded-lg ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                          >
                            <CloseIcon size={16} />
                          </button>
                        </div>
                      ) : (
                        <div
                          onClick={() =>
                            handleInlineEdit("location", appointment.location)
                          }
                          className={`cursor-pointer p-2 rounded transition-colors ${isDarkMode ? "hover:bg-gray-800/50" : "hover:bg-gray-100"}`}
                        >
                          <p
                            className={
                              isDarkMode ? "text-white" : "text-gray-900"
                            }
                          >
                            {appointment.location}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {appointment.meetingUrl && (
                    <div>
                      <label
                        className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Meeting URL
                      </label>
                      <a
                        href={appointment.meetingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                      >
                        {appointment.meetingUrl}
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  )}
                  {appointment.estimatedTravelTime && (
                    <div>
                      <label
                        className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        Estimated Travel Time
                      </label>
                      <p
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {appointment.estimatedTravelTime} minutes
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {appointment.description && (
                <div
                  className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                >
                  <h3
                    className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Description
                  </h3>
                  {editingField === "description" ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveInlineEdit}
                          disabled={savingField}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm disabled:opacity-50"
                        >
                          {savingField ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={handleCancelInlineEdit}
                          className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p
                      onClick={() =>
                        handleInlineEdit("description", appointment.description)
                      }
                      className={`whitespace-pre-wrap cursor-pointer p-2 rounded transition-colors ${isDarkMode ? "text-gray-300 hover:bg-gray-800/50" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      {appointment.description}
                    </p>
                  )}
                </div>
              )}

              {/* Notes */}
              {appointment.notes && (
                <div
                  className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                >
                  <h3
                    className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    Internal Notes
                  </h3>
                  {editingField === "notes" ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingValue}
                        onChange={(e) => setEditingValue(e.target.value)}
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:border-blue-500 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"}`}
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveInlineEdit}
                          disabled={savingField}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm disabled:opacity-50"
                        >
                          {savingField ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={handleCancelInlineEdit}
                          className={`px-4 py-2 rounded-lg text-sm ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p
                      onClick={() =>
                        handleInlineEdit("notes", appointment.notes)
                      }
                      className={`whitespace-pre-wrap cursor-pointer p-2 rounded transition-colors ${isDarkMode ? "text-gray-300 hover:bg-gray-800/50" : "text-gray-600 hover:bg-gray-100"}`}
                    >
                      {appointment.notes}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div
                className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Quick Actions
                </h3>
                {/* Fetch Calls from External System */}
                <div
                  className={`rounded-lg border p-4 mb-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                >
                  <h3
                    className={`font-semibold mb-3 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <PhoneCall size={16} className="text-blue-400" />
                    Call Lookup
                  </h3>
                  <p
                    className={`text-xs mb-3 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Search the external call system for calls matching this
                    appointment's phone numbers.
                  </p>

                  <button
                    onClick={handleFetchCalls}
                    disabled={callFetch.status === "loading"}
                    className="w-full px-3 py-2 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 border border-blue-500/30 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                  >
                    {callFetch.status === "loading" ? (
                      <>
                        <Loader size={14} className="animate-spin" />{" "}
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search size={14} /> Fetch Calls
                      </>
                    )}
                  </button>

                  {callFetch.status === "done" && callFetch.result && (
                    <div className="mt-3 space-y-2">
                      <div className="flex items-center gap-2 text-xs text-green-400 bg-green-500/10 border border-green-500/20 rounded-lg px-3 py-2">
                        <CheckCircle size={13} />
                        {callFetch.result.stored} new ·{" "}
                        {callFetch.result.skipped} already existed
                      </div>
                      {callFetch.result.calls?.length > 0 && (
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                          {callFetch.result.calls.map((c) => (
                            <div
                              key={c.callId}
                              className={`text-xs rounded px-3 py-1.5 ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                            >
                              <p
                                className={
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }
                              >
                                {c.phoneNumber}
                              </p>
                              <p
                                className={
                                  isDarkMode ? "text-gray-500" : "text-gray-400"
                                }
                              >
                                {c.callDate
                                  ? new Date(c.callDate).toLocaleDateString()
                                  : "—"}
                                {c.duration
                                  ? ` · ${Math.round(c.duration / 60)}m`
                                  : ""}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {callFetch.status === "error" && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                      <XCircle size={13} />
                      {callFetch.error}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {appointment.status === "scheduled" && (
                    <button
                      onClick={() => handleStatusChange("confirmed")}
                      className="w-full px-4 py-2 bg-green-600/10 hover:bg-green-600/20 text-green-400 border border-green-500/30 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <CheckCircle size={16} />
                      Confirm Appointment
                    </button>
                  )}
                  {["scheduled", "confirmed"].includes(appointment.status) && (
                    <>
                      <button
                        onClick={() => handleStatusChange("completed")}
                        className="w-full px-4 py-2 bg-purple-600/10 hover:bg-purple-600/20 text-purple-400 border border-purple-500/30 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        <CheckCircle size={16} />
                        Mark as Completed
                      </button>
                      <button
                        onClick={() => handleStatusChange("cancelled")}
                        className="w-full px-4 py-2 bg-red-600/10 hover:bg-red-600/20 text-red-400 border border-red-500/30 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                      >
                        <XCircle size={16} />
                        Cancel Appointment
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Assigned To */}
              <div
                className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Assignment
                </h3>
                <div className="space-y-3">
                  <div>
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Assigned To
                    </label>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${isDarkMode ? "bg-blue-500/10 text-blue-400" : "bg-blue-100 text-blue-600"}`}
                      >
                        {appointment.assignee?.name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p
                          className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {appointment.assignee?.name || "Unassigned"}
                        </p>
                        <p
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {appointment.assignee?.email}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label
                      className={`text-sm block mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Created By
                    </label>
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${isDarkMode ? "bg-gray-500/10 text-gray-400" : "bg-gray-100 text-gray-600"}`}
                      >
                        {appointment.creator?.name?.charAt(0) || "?"}
                      </div>
                      <div>
                        <p
                          className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {appointment.creator?.name || "Unknown"}
                        </p>
                        <p
                          className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {new Date(appointment.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Reminders */}
              {appointment.reminders && appointment.reminders.length > 0 && (
                <div
                  className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                >
                  <h3
                    className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    <Bell size={18} className="text-blue-400" />
                    Reminders
                  </h3>
                  <div className="space-y-2">
                    {appointment.reminders.map((reminder, index) => (
                      <div
                        key={index}
                        className={`flex items-center gap-2 text-sm p-2 rounded ${isDarkMode ? "bg-gray-800/50" : "bg-gray-100"}`}
                      >
                        <Clock
                          size={14}
                          className={
                            isDarkMode ? "text-gray-400" : "text-gray-500"
                          }
                        />
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }
                        >
                          {reminder.minutes} min before -{" "}
                          {reminder.type.charAt(0).toUpperCase() +
                            reminder.type.slice(1)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* System Info */}
              <div
                className={`rounded-lg border p-4 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  System Information
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      Created
                    </span>
                    <span
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {new Date(appointment.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      Last Updated
                    </span>
                    <span
                      className={isDarkMode ? "text-white" : "text-gray-900"}
                    >
                      {new Date(appointment.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                  {appointment.source && (
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        Source
                      </span>
                      <span
                        className={`capitalize ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {appointment.source}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      Visibility
                    </span>
                    <span
                      className={`capitalize ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {appointment.visibility}
                    </span>
                  </div>
                  {appointment.saleOriginated && (
                    <div className="flex justify-between">
                      <span
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      >
                        Sale Originated
                      </span>
                      <span
                        className={isDarkMode ? "text-white" : "text-gray-900"}
                      >
                        {formatSaleOriginated(appointment.saleOriginated)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "attendees" && (
          <div className="max-w-4xl">
            <div
              className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div className="flex items-center justify-between mb-6">
                <h3
                  className={`font-semibold text-lg ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  Attendees
                </h3>
                <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors">
                  <Plus size={16} />
                  Add Attendee
                </button>
              </div>

              {attendees.length > 0 ? (
                <div className="space-y-3">
                  {attendees.map((attendee) => (
                    <div
                      key={attendee.id}
                      className={`flex items-center justify-between p-4 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${isDarkMode ? "bg-blue-500/10 text-blue-400" : "bg-blue-100 text-blue-600"}`}
                        >
                          {attendee.name?.charAt(0) ||
                            attendee.email?.charAt(0) ||
                            "?"}
                        </div>
                        <div>
                          <p
                            className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {attendee.name || "Unknown"}
                          </p>
                          <p
                            className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {attendee.email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {attendee.status && (
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              attendee.status === "accepted"
                                ? "bg-green-500/10 text-green-400 border border-green-500/30"
                                : attendee.status === "declined"
                                  ? "bg-red-500/10 text-red-400 border border-red-500/30"
                                  : "bg-gray-500/10 text-gray-400 border border-gray-500/30"
                            }`}
                          >
                            {attendee.status}
                          </span>
                        )}
                        {attendee.isOptional && (
                          <span
                            className={`text-xs px-2 py-1 rounded ${isDarkMode ? "bg-gray-700 text-gray-400" : "bg-gray-200 text-gray-500"}`}
                          >
                            Optional
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users
                    size={48}
                    className={`mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <p
                    className={`mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No attendees added yet
                  </p>
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium">
                    Add First Attendee
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "related" && (
          <div className="max-w-4xl space-y-6">
            {/* Related Lead */}
            {appointment.lead && (
              <div
                className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <User size={18} className="text-blue-400" />
                  Related Lead
                </h3>
                <button
                  onClick={() => navigate(`/leads/${appointment.lead.id}`)}
                  className={`w-full text-left p-4 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-400 flex items-center gap-2">
                        {appointment.lead.name}
                        <ExternalLink size={14} />
                      </p>
                      <p
                        className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                      >
                        {appointment.lead.email}
                      </p>
                      {appointment.lead.company && (
                        <p
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {appointment.lead.company}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Related Account */}
            {appointment.account && (
              <div
                className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Building2 size={18} className="text-blue-400" />
                  Related Account
                </h3>
                <button
                  onClick={() =>
                    navigate(`/accounts/${appointment.account.id}`)
                  }
                  className={`w-full text-left p-4 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-400 flex items-center gap-2">
                        {appointment.account.name}
                        <ExternalLink size={14} />
                      </p>
                      {appointment.account.industry && (
                        <p
                          className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {appointment.account.industry}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Related Opportunity */}
            {appointment.opportunity && (
              <div
                className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Target size={18} className="text-blue-400" />
                  Related Opportunity
                </h3>
                <button
                  onClick={() =>
                    navigate(`/opportunities/${appointment.opportunity.id}`)
                  }
                  className={`w-full text-left p-4 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-400 flex items-center gap-2">
                        {appointment.opportunity.name}
                        <ExternalLink size={14} />
                      </p>
                      {appointment.opportunity.stage && (
                        <p
                          className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Stage: {appointment.opportunity.stage}
                        </p>
                      )}
                      {appointment.opportunity.amount && (
                        <p
                          className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          ${appointment.opportunity.amount.toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* Related Case */}
            {appointment.case && (
              <div
                className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
              >
                <h3
                  className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Briefcase size={18} className="text-blue-400" />
                  Related Case
                </h3>
                <button
                  onClick={() => navigate(`/cases/${appointment.case.id}`)}
                  className={`w-full text-left p-4 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800/50 hover:bg-gray-800" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-blue-400 flex items-center gap-2">
                        {appointment.case.subject ||
                          `Case #${appointment.case.caseNumber}`}
                        <ExternalLink size={14} />
                      </p>
                      {appointment.case.status && (
                        <p
                          className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          Status: {appointment.case.status}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* No Related Records */}
            {!appointment.lead &&
              !appointment.account &&
              !appointment.opportunity &&
              !appointment.case && (
                <div
                  className={`rounded-lg border p-12 text-center ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
                >
                  <FileText
                    size={48}
                    className={`mx-auto mb-3 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
                    No related records found
                  </p>
                </div>
              )}
          </div>
        )}
        {activeTab === "activity" && (
          <div className="max-w-4xl">
            <div
              className={`rounded-lg border p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
            >
              <div className="flex items-center justify-between mb-6">
                <h3
                  className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  <Activity size={18} className="text-blue-400" />
                  Activity Timeline
                </h3>
                <button
                  onClick={fetchActivity}
                  disabled={activityLoading}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${isDarkMode ? "bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white" : "bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-900"} disabled:opacity-50`}
                >
                  <RefreshCw
                    size={13}
                    className={activityLoading ? "animate-spin" : ""}
                  />
                  Refresh
                </button>
              </div>

              <ActivityFeed
                items={activityItems}
                loading={activityLoading}
                emptyMessage="No activity has been recorded for this appointment yet."
                maxHeight="max-h-[480px]"
              />
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal Placeholder */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className={`rounded-lg border max-w-2xl w-full p-6 ${isDarkMode ? "bg-[#13161B] border-gray-700" : "bg-white border-gray-200"}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                Edit Appointment
              </h3>
              <button
                onClick={() => setShowEditModal(false)}
                className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
              >
                <CloseIcon size={20} />
              </button>
            </div>
            <p
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Edit modal would go here - implement EditAppointmentModal
              component
            </p>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"}`}
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentDetail;
